import React from 'react'
import { Theme } from '@emotion/react'
import {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  SxProps,
  Autocomplete,
  TextField,
} from '@mui/material'

type ComboBoxProps<T> = {
  id?: string
  options: Array<T>
  getOptionLabel: (option: T) => string
  getOptionKey?: (option: T) => string
  selectedOption: T | null
  onSelection: (
    event: React.SyntheticEvent,
    value: T | null,
    reason: AutocompleteChangeReason,
  ) => void
  onInputChange?: (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => void
  label?: string
  boxStyle?: SxProps<Theme>
  disabled?: boolean
  loading?: boolean
  inputValue?: string
  noOptionsText?: React.ReactNode
  loadingText?: React.ReactNode
  width?: number | string
}

const ComboBox = <T,>({
  id = 'combo-box',
  options,
  getOptionLabel,
  getOptionKey,
  selectedOption,
  onSelection,
  onInputChange,
  label,
  boxStyle,
  disabled = false,
  loading = false,
  inputValue,
  noOptionsText,
  loadingText,
  width,
}: ComboBoxProps<T>) => {
  /**
   * Clé stable pour comparer option <-> value
   * fallback sur le label si aucune key n’est fournie
   */
  const getKey = React.useCallback(
    (option: T) => (getOptionKey ? getOptionKey(option) : getOptionLabel(option)),
    [getOptionKey, getOptionLabel],
  )

  return (
    <Autocomplete
      disablePortal
      id={id}
      options={options}
      value={selectedOption}
      onChange={onSelection}
      onInputChange={onInputChange}
      disabled={disabled}
      loading={loading}
      inputValue={inputValue}
      getOptionLabel={getOptionLabel}
      clearOnBlur={false}
      noOptionsText={noOptionsText}
      loadingText={loadingText}
      isOptionEqualToValue={(option, value) => value != null && getKey(option) === getKey(value)}
      renderOption={(props, option) => (
        <li
          {...props}
          key={getKey(option)}
        >
          {getOptionLabel(option)}
        </li>
      )}
      sx={{
        width: width ?? 250,
        ...(boxStyle || {}),
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          {...(label ? { label } : {})}
          sx={{ marginLeft: '5px', padding: 0 }}
          size="small"
        />
      )}
    />
  )
}

export default ComboBox
